import * as React from "react";
import { Checkbox as AriaCheckbox, type CheckboxProps as AriaCheckboxProps } from "react-aria-components";
import { CheckIcon } from "lucide-react";

import { cn } from "./utils";

export interface CheckboxProps extends Omit<AriaCheckboxProps, "className" | "onChange"> {
  className?: string;
  /** @deprecated Use isSelected instead */
  checked?: boolean;
  /** @deprecated Use onChange instead */
  onCheckedChange?: (checked: boolean) => void;
  onChange?: (isSelected: boolean) => void;
}

function Checkbox({ className, children, checked, onCheckedChange, onChange, isSelected, ...props }: CheckboxProps) {
  // Support both old Radix API (checked/onCheckedChange) and new React Aria API (isSelected/onChange)
  const selectedValue = isSelected ?? checked;
  const handleChange = (value: boolean) => {
    onChange?.(value);
    onCheckedChange?.(value);
  };

  return (
    <AriaCheckbox
      data-slot="checkbox"
      className={cn(
        "group peer flex items-center gap-2",
        className,
      )}
      isSelected={selectedValue}
      onChange={handleChange}
      {...props}
    >
      {({ isSelected, isIndeterminate }) => (
        <>
          <div
            data-slot="checkbox-box"
            className={cn(
              "border bg-input-background dark:bg-input/30 size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow flex items-center justify-center",
              "group-focus-visible:border-ring group-focus-visible:ring-ring/50 group-focus-visible:ring-[3px]",
              "group-disabled:cursor-not-allowed group-disabled:opacity-50",
              "group-invalid:ring-destructive/20 dark:group-invalid:ring-destructive/40 group-invalid:border-destructive",
              isSelected && "bg-primary dark:bg-primary text-primary-foreground dark:text-primary-foreground border-primary",
            )}
          >
            {isSelected && <CheckIcon className="size-3.5" />}
          </div>
          {children}
        </>
      )}
    </AriaCheckbox>
  );
}

export { Checkbox };
